import React, { useEffect, useRef } from 'react'
import { get, isEmpty, isNull, map, uniqueId } from 'lodash'
import moment from 'moment'
import { Field, FieldArray, Form, Formik } from 'formik'
import { ObjectSchema } from 'yup'
import cn from 'classnames'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'

import { FormModalScreen } from 'components/FormModal'
import {
  StyledKeyboardDatePicker,
  StyledKeyboardTimePicker,
} from 'civic-champs-shared/formik/components/StyledFormikDateTimePickers'
import { useAddEditEventStyles } from 'Event/hooks/useAddEditEventStyles'
import AdvancedAutocomplete from 'civic-champs-shared/core/AdvancedAutocomplete'
import { OpportunityTemplate } from 'volunteering/opportunities/interfaces'
import useGetOpportunityTemplates from 'volunteering/opportunities/hooks/useGetOpportunityTemplates'
import { RECURRING_TYPE } from 'civic-champs-shared/core/utils/constants/RECURRING_TYPE_OPTIONS'
import DeleteIcon from '@material-ui/icons/Delete'
import { Role } from 'volunteer-role/types'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import { InlineAddButton } from 'civic-champs-shared/core/InlineAddButton'
import { FormRole, createEmptyFormRole, toLocalDate } from 'Event/helpers/add-edit-helpers'
import { useFeatureEnabled } from 'core/feature/hooks'
import RadioGroupFormField from 'components/RadioGroupFormField'
import { LegacySchedulableBlock } from './add-edit-event-dates/LegacySchedulableBlock'
import {
  MONTHLY_RECURRING_TYPE,
  SIMPLE_RECURRING_TYPE,
} from '../../civic-champs-shared/core/utils/constants/SIMPLE_RECURRING_TYPE_OPTIONS'
import { SchedulableBlock } from './add-edit-event-dates/SchedulableBlock'
import {
  editEventRolesPayloadSchema,
  eventDatePayloadSchema,
  simpleEventDatePayloadSchema,
} from '../validation/eventDatePayloadSchema'
import { SchedulableEditBlock } from './add-edit-event-dates/SchedulableEditBlock'
import { NumberField } from './NumberField'
import { RoleAutocompleteField } from './RoleAutocompleteField'
import CheckboxField from 'civic-champs-shared/formik/components/CheckboxField'

const getRoleRowDragStyle = (
  isDragging: boolean,
  style: React.CSSProperties | undefined,
  container: HTMLDivElement | null,
) => {
  if (!isDragging || !style || !container) {
    return style
  }

  const { left, width } = container.getBoundingClientRect()

  return {
    ...style,
    left,
    width,
    opacity: 1,
    background: '#F8FAFF',
    boxSizing: 'border-box' as const,
  }
}

export interface EventDateScreenValues {
  startDate: Date | null
  dates: Date[]
  endDate: Date | null
  startTime: Date
  endTime: Date
  opportunityTemplate: OpportunityTemplate | null
  isSchedulable: boolean
  recurringType?: RECURRING_TYPE
  simpleRecurringType: SIMPLE_RECURRING_TYPE
  roles: FormRole[]
  useTemplate: boolean
  monthlyRecurringType: MONTHLY_RECURRING_TYPE
  saveAsDraft: boolean
  allowMultipleRegistrations: boolean
}

export interface EventDateScreenProps {
  values: EventDateScreenValues
  onSubmit: (values: EventDateScreenValues) => void
  onBack: () => void
  isNewEvent: boolean
  isDraftable: boolean
  roles: Role[]
  addRole: () => Promise<Role>
  onOpportunityTemplateChange?: (opportunityTemplate: OpportunityTemplate | null) => void
}

export const EventDateAndRoleScreen = ({
  values,
  onSubmit,
  onBack,
  isNewEvent,
  roles: allRoles,
  addRole,
  isDraftable,
  onOpportunityTemplateChange,
}: EventDateScreenProps) => {
  const isCalendarEventSwitchEnabled = useFeatureEnabled('NewAddEventUICalendarEvent')
  const isAllowEditShiftsWithRegistrationsEnabled = useFeatureEnabled('ChangeTimeForShiftsWithRegistrations')
  const isSimplifyRecurringEventRulesEnabled = useFeatureEnabled('SimplifyRecurringEventRules')
  const isMaintainEventSequencesAcrossEdits = useFeatureEnabled('MaintainEventSequencesAcrossEdits')
  const isAllowMultipleRegistrationsPerVolunteerEnabled = useFeatureEnabled('AllowMultipleRegistrationsPerVolunteer')
  const classes = useAddEditEventStyles()
  const rolesDropContainerRef = useRef<HTMLDivElement | null>(null)

  const [getOpportunityTemplates, { result: opportunityTemplates }] =
    useGetOpportunityTemplates(isCalendarEventSwitchEnabled)
  useEffect(() => {
    if (isNewEvent) {
      getOpportunityTemplates(true, true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  let validationSchema: ObjectSchema
  if (isNewEvent) {
    validationSchema = isSimplifyRecurringEventRulesEnabled ? simpleEventDatePayloadSchema : eventDatePayloadSchema
  } else {
    validationSchema = editEventRolesPayloadSchema
  }

  return (
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      isInitialValid={!isNewEvent}
      validateOnChange
    >
      {({ submitForm, isSubmitting, values, setFieldValue, errors, setValues }) => {
        return (
          <Form>
            <FormModalScreen
              onBack={onBack}
              onNext={submitForm}
              onDraft={
                values.isSchedulable && isDraftable
                  ? () => {
                      setFieldValue('saveAsDraft', true, false)
                      submitForm()
                    }
                  : undefined
              }
              nextText="Next"
              draftText={values.isSchedulable && isDraftable ? 'Finish Later' : undefined}
              backText="Cancel"
              disabled={isSubmitting}
            >
              {isNewEvent && opportunityTemplates.length !== 0 && (
                <div className={cn(classes.section, classes.subSection)}>
                  <span className={classes.sectionTitle}>Event Details:</span>
                  <RadioGroupFormField
                    valueIsBoolean
                    name="useTemplate"
                    disabled={isSubmitting}
                    options={[
                      {
                        label: 'Create event from a Template',
                        value: true,
                      },
                    ]}
                  />
                  {values.useTemplate && (
                    <div className={classes.subDropdown}>
                      <AdvancedAutocomplete<OpportunityTemplate>
                        label={'Event Template'}
                        options={opportunityTemplates}
                        placeholder={'Select an Event Template'}
                        multiline={false}
                        value={values.opportunityTemplate}
                        getOptionInfo={template => ({
                          left: template.name,
                          id: template.id,
                        })}
                        onChange={opportunityTemplate => {
                          //TODO all this similar logic should probably be in one place...
                          // do this logic HERE instead of in the submit handler so it doesn't override post-selection changes
                          const selectedTemplate = Array.isArray(opportunityTemplate) ? null : opportunityTemplate
                          if (selectedTemplate !== null) {
                            const { startTime, endTime, roles } = selectedTemplate

                            if (!isNull(startTime) || !isNull(endTime)) {
                              setFieldValue('startTime', toLocalDate(startTime))
                              setFieldValue('endTime', toLocalDate(endTime))
                            }

                            if (!isEmpty(roles)) {
                              const formRoles: FormRole[] = map(roles, ({ role, quantity }) => ({
                                roleId: role.id,
                                quantity,
                                registrations: 0,
                                itemId: uniqueId('role_'),
                              }))
                              setFieldValue('roles', formRoles)
                            }
                          }

                          setFieldValue('opportunityTemplate', selectedTemplate)
                          onOpportunityTemplateChange?.(selectedTemplate)
                        }}
                      />
                    </div>
                  )}
                  <RadioGroupFormField
                    valueIsBoolean
                    name="useTemplate"
                    disabled={isSubmitting}
                    unGap={!values.useTemplate}
                    options={[
                      {
                        label: 'Create event from Scratch',
                        value: false,
                      },
                    ]}
                  />
                </div>
              )}

              {isNewEvent && (
                <div className={classes.section}>
                  {!values.isSchedulable ||
                    ((isNewEvent || isAllowEditShiftsWithRegistrationsEnabled) && isCalendarEventSwitchEnabled && (
                      <span className={classes.sectionTitle}>Event Time:</span>
                    ))}
                  {(isNewEvent || isAllowEditShiftsWithRegistrationsEnabled) &&
                    isCalendarEventSwitchEnabled &&
                    (!isSimplifyRecurringEventRulesEnabled ? (
                      <LegacySchedulableBlock values={values} setFieldValue={setFieldValue} errors={errors} />
                    ) : (
                      <SchedulableBlock values={values} setFieldValue={setFieldValue} errors={errors} />
                    ))}
                  {!values.isSchedulable && (
                    <>
                      <div className={classes.dateTimePickers}>
                        <div className={classes.dateTimePicker}>
                          <Field
                            inputVariant="outlined"
                            label="Start Date *"
                            higher
                            component={StyledKeyboardDatePicker}
                            name="startDate"
                          />
                        </div>

                        <div className={classes.dateTimePicker}>
                          <Field
                            higher
                            inputVariant="outlined"
                            label="End Date *"
                            component={StyledKeyboardDatePicker}
                            name="endDate"
                            minDate={moment(values.startDate || undefined)
                              .startOf('day')
                              .toDate()}
                            minDateMessage={'The end date cannot occur before the start date.'}
                          />
                        </div>
                      </div>

                      <div className={classes.dateTimePickers}>
                        <div className={classes.dateTimePicker}>
                          <Field
                            inputVariant="outlined"
                            label="Start Time *"
                            higher
                            component={StyledKeyboardTimePicker}
                            name="startTime"
                          />
                        </div>

                        <div className={classes.dateTimePicker}>
                          <Field
                            higher
                            inputVariant="outlined"
                            label="End Time *"
                            component={StyledKeyboardTimePicker}
                            name="endTime"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              {values.isSchedulable && (
                <>
                  <div className={classes.section}>
                    <span className={classes.sectionTitle}>Roles:</span>
                    <FieldArray name="roles">
                      {({ remove, push, move }) => {
                        const canReorderRoles = values.roles.length > 1

                        const renderRoleRow = (
                          role: FormRole,
                          index: number,
                          dragHandleProps?: React.HTMLAttributes<HTMLElement>,
                        ) => (
                          <div className={classes.role}>
                            {canReorderRoles && (
                              <span {...dragHandleProps} className={classes.roleDragHandle}>
                                <DragIndicatorIcon />
                              </span>
                            )}
                            <div className={classes.roleSelector}>
                              <Field
                                name={`roles[${index}].roleId`}
                                component={RoleAutocompleteField}
                                roles={allRoles}
                                addRole={addRole}
                                label={'Role'}
                                placeholder={'Select a Role'}
                                addNewText={'Add a new role'}
                                addNewIcon={<AddCircleIcon className={classes.addIcon} />}
                              />
                            </div>
                            <div className={classes.roleQuantity}>
                              <Field
                                name={`roles[${index}].quantity`}
                                component={NumberField}
                                label={'Max'}
                                placeholder={'Number'}
                              />
                            </div>
                            <DeleteIcon
                              className={classes.roleDelete}
                              onClick={() => {
                                remove(index)
                              }}
                            />
                          </div>
                        )

                        const handleDragEnd = (result: DropResult) => {
                          if (!result.destination) {
                            return
                          }

                          move(result.source.index, result.destination.index)
                        }

                        const container = rolesDropContainerRef.current

                        return (
                          <div className={classes.section}>
                            {canReorderRoles ? (
                              <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="event-roles">
                                  {droppableProvided => (
                                    <div
                                      className={classes.rolesDropContainer}
                                      {...droppableProvided.droppableProps}
                                      ref={node => {
                                        rolesDropContainerRef.current = node
                                        droppableProvided.innerRef(node)
                                      }}
                                    >
                                      {values.roles.map((role, index) => (
                                        <Draggable key={role.itemId} draggableId={role.itemId} index={index}>
                                          {(provided, snapshot) => (
                                            <div
                                              className={cn(
                                                classes.roleRow,
                                                snapshot.isDragging && classes.roleRowDragging,
                                              )}
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              style={getRoleRowDragStyle(
                                                snapshot.isDragging,
                                                provided.draggableProps.style,
                                                container,
                                              )}
                                            >
                                              {renderRoleRow(role, index, provided.dragHandleProps)}
                                            </div>
                                          )}
                                        </Draggable>
                                      ))}
                                      {droppableProvided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            ) : (
                              values.roles.map((role, index) => (
                                <div className={classes.roleRow} key={role.itemId}>
                                  {renderRoleRow(role, index)}
                                </div>
                              ))
                            )}
                            <div className={classes.rolesFooter}>
                              <InlineAddButton onClick={() => push(createEmptyFormRole())} text="Add Role" />
                              {isAllowMultipleRegistrationsPerVolunteerEnabled && (
                                <Field
                                  name="allowMultipleRegistrations"
                                  component={CheckboxField}
                                  className={classes.allowMultipleRegistrationsCheckbox}
                                  label="Allow multiple registrations per volunteer"
                                />
                              )}
                            </div>
                          </div>
                        )
                      }}
                    </FieldArray>
                  </div>
                  {isMaintainEventSequencesAcrossEdits && isSimplifyRecurringEventRulesEnabled && !isNewEvent && (
                    <div className={classes.section}>
                      <span className={classes.sectionTitle}>Scheduling:</span>
                      <SchedulableEditBlock />
                    </div>
                  )}
                </>
              )}
            </FormModalScreen>
          </Form>
        )
      }}
    </Formik>
  )
}
